import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    ReferenceArea, ReferenceLine,
} from "recharts";
import { fetchCombined, fetchThresholds, fetchFarm } from "@/lib/api";
import { fmt, RISK_COLORS } from "@/lib/risk";
import { stationColor } from "@/lib/stationColors";
import { cn } from "@/lib/utils";

const RANGES = [
    { key: "1h",  label: "1h"  },
    { key: "3h",  label: "3h"  },
    { key: "6h",  label: "6h"  },
    { key: "12h", label: "12h" },
    { key: "24h", label: "24h" },
    { key: "3d",  label: "3d"  },
    { key: "7d",  label: "7d"  },
];

// Station line colours come from lib/stationColors so HFDI trend, Weather
// over time, YTD rainfall and any other multi-station chart share one palette.

function shortTick(ts, range) {
    const d = new Date(ts * 1000);
    // Multi-day ranges: show date only. Sub-day ranges: show time.
    if (range === "24h" || range === "3d" || range === "7d") {
        return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
    }
    return d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fullTs(ts) {
    return new Date(ts * 1000).toLocaleString("en-AU", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
    });
}

/**
 * Compute X-axis tick timestamps.
 * For multi-day ranges we snap to local midnight of each day so the axis
 * shows one clear date label per day instead of arbitrary times.
 */
function computeXTicks(rows, range) {
    if (!rows.length) return undefined;
    if (range !== "24h" && range !== "3d" && range !== "7d") return undefined;
    const first = new Date(rows[0].ts * 1000);
    const last  = new Date(rows[rows.length - 1].ts * 1000);
    const ticks = [];
    const cur = new Date(first);
    cur.setHours(0, 0, 0, 0);
    if (cur < first) cur.setDate(cur.getDate() + 1); // start from first FULL local day
    while (cur <= last) {
        ticks.push(Math.floor(cur.getTime() / 1000));
        cur.setDate(cur.getDate() + 1);
    }
    return ticks;
}

/**
 * HFDI over time with McArthur-style green/orange/red risk zones as
 * background bands and a dashed "CEASE HARVEST THRESHOLD" line at HFDI 35.
 *
 * Props:
 *   station     – optional station id; when set the card locks to that
 *                 station and hides the selector (used on Station Detail).
 *   compact     – tighter card padding on the station detail page.
 */
export default function HfdiTrendCard({ station: fixedStation, compact = false }) {
    const [range, setRange] = useState("3d");
    const [pickerStation, setPickerStation] = useState("all");
    const stationParam = fixedStation ?? pickerStation;

    const { data: farm } = useQuery({ queryKey: ["farm"], queryFn: fetchFarm });
    const { data: thr } = useQuery({ queryKey: ["thresholds"], queryFn: fetchThresholds });
    const { data, isLoading } = useQuery({
        queryKey: ["combined", range, stationParam],
        queryFn: () => fetchCombined(range, stationParam),
        refetchInterval: 60_000,
    });

    const bands = useMemo(() => {
        const f = thr?.fdi;
        if (!f) return null;
        return {
            green:  f.green  ?? 15,
            yellow: f.yellow ?? 25,
            orange: f.orange ?? 35,   // === harvest threshold (visual red starts)
            max:    f.scale_max ?? 75,
            harvest: f.harvest_threshold ?? f.orange ?? 35,
        };
    }, [thr]);

    const stationOptions = useMemo(() => {
        const list = (farm?.stations || []).filter((s) => !s.is_placeholder);
        return [{ id: "all", name: "All Stations" }, ...list];
    }, [farm]);

    // Flatten into rows keyed by ts with one series per station.
    const { rows, seriesKeys, domain } = useMemo(() => {
        if (!data?.stations) return { rows: [], seriesKeys: [], domain: [0, 60] };
        const map = new Map();
        const keys = [];
        let maxSeen = bands?.harvest ?? 35;
        data.stations.forEach((st, i) => {
            const key = `fdi__${st.id}`;
            keys.push({ id: st.id, name: st.name, seriesKey: key,
                stroke: stationColor(st.id, i) });
            st.series.forEach((pt) => {
                if (!map.has(pt.ts)) map.set(pt.ts, { ts: pt.ts });
                map.get(pt.ts)[key] = pt.fdi;
                if (pt.fdi !== null && pt.fdi !== undefined) {
                    if (pt.fdi > maxSeen) maxSeen = pt.fdi;
                }
            });
        });
        const rows = Array.from(map.values()).sort((a, b) => a.ts - b.ts);
        const top = Math.max(maxSeen + 5, (bands?.harvest ?? 35) + 10, bands?.max ?? 75);
        return { rows, seriesKeys: keys, domain: [0, Math.min(top, (bands?.max ?? 75) + 10)] };
    }, [data, bands]);

    return (
        <div className={cn("fw-card", compact ? "p-3 sm:p-4" : "p-4")} data-testid="home-hfdi-trend">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                    <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
                        Harvest Fire Danger over time
                    </h2>
                    <p className="text-[11px] text-slate-500 fw-metric">
                        HFDI · cease-harvest threshold {bands?.harvest ?? 35}
                        {data?.bucket_minutes ? ` · ${data.bucket_minutes}-min buckets` : ""}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {!fixedStation && (
                        <select
                            value={pickerStation}
                            onChange={(e) => setPickerStation(e.target.value)}
                            data-testid="hfdi-station"
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700"
                        >
                            {stationOptions.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    )}
                    <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
                        {RANGES.map((r) => (
                            <button key={r.key}
                                data-testid={`hfdi-range-${r.key}`}
                                onClick={() => setRange(r.key)}
                                className={cn(
                                    "px-2 py-1 text-[11px] font-bold rounded-md transition-colors",
                                    range === r.key
                                        ? "bg-white text-slate-900 shadow-sm"
                                        : "text-slate-500 hover:text-slate-800"
                                )}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="h-56 sm:h-64" data-testid="hfdi-chart">
                {isLoading || !rows.length || !bands ? (
                    <div className="h-full grid place-items-center text-xs text-slate-400">
                        {isLoading ? "Loading…" : "No HFDI data for this range"}
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={rows} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            {/* Risk-band backgrounds — draw beneath the lines */}
                            <ReferenceArea y1={0}            y2={bands.green}  fill={RISK_COLORS.green.bg}  fillOpacity={0.9} ifOverflow="extendDomain" />
                            <ReferenceArea y1={bands.green}  y2={bands.yellow} fill={RISK_COLORS.yellow.bg} fillOpacity={0.9} ifOverflow="extendDomain" />
                            <ReferenceArea y1={bands.yellow} y2={bands.orange} fill={RISK_COLORS.orange.bg} fillOpacity={0.9} ifOverflow="extendDomain" />
                            <ReferenceArea y1={bands.orange} y2={domain[1]}    fill={RISK_COLORS.red.bg}    fillOpacity={0.9} ifOverflow="extendDomain" />
                            {/* Cease-harvest dashed line */}
                            <ReferenceLine y={bands.harvest} stroke="#0F172A" strokeDasharray="6 4" strokeWidth={1.5}
                                label={{ value: `Cease harvest ≥ ${bands.harvest}`, position: "insideTopRight",
                                    fill: "#0F172A", fontSize: 10, fontWeight: 700 }} />
                            <XAxis dataKey="ts" type="number" scale="time"
                                domain={["dataMin", "dataMax"]}
                                ticks={computeXTicks(rows, range)}
                                tickFormatter={(v) => shortTick(v, range)}
                                tick={{ fontSize: 11, fontWeight: 600, fill: "#334155" }}
                                tickMargin={6}
                                minTickGap={20} />
                            <YAxis domain={domain}
                                ticks={[0, bands.green, bands.yellow, bands.harvest, domain[1]]}
                                allowDecimals={false}
                                tick={{ fontSize: 11, fontWeight: 600, fill: "#334155" }}
                                width={34}
                                tickMargin={4}
                                tickFormatter={(v) => `${v}`}
                                label={{ value: "HFDI", angle: -90, position: "insideLeft",
                                    style: { fontSize: 10, fill: "#64748B", fontWeight: 700 }, offset: 16 }} />
                            <Tooltip
                                labelFormatter={fullTs}
                                contentStyle={{ background: "white", border: "1px solid #e2e8f0",
                                    borderRadius: 12, fontSize: 12 }}
                                formatter={(value, name, props) => {
                                    if (value === null || value === undefined) return ["–", name];
                                    const meta = seriesKeys.find((k) => k.seriesKey === props.dataKey);
                                    return [`${fmt(value, 1)}`, meta?.name || name];
                                }}
                            />
                            {seriesKeys.map((k) => (
                                <Line key={k.seriesKey} type="monotone"
                                    dataKey={k.seriesKey} stroke={k.stroke}
                                    strokeWidth={2} dot={false} activeDot={{ r: 3 }}
                                    connectNulls isAnimationActive={false} />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>

            {seriesKeys.length > 1 && (
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    {seriesKeys.map((k) => (
                        <div key={k.seriesKey} className="inline-flex items-center gap-1 text-[10px] text-slate-600 fw-metric">
                            <span className="h-2 w-2 rounded-full" style={{ background: k.stroke }} />
                            {k.name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
